/* Immersive landing + size grid.

   THE CHOSEN SIZE IS OURS. Everything here that used to ask the theme's <select> what the
   customer had chosen now asks this module instead, because the theme's answer is not
   reliable in either direction:

   - it says a size is chosen when none is. Shopify renders the page for
     selected_or_first_available_variant, so on a plain visit the select already reads
     EU 35. Read back, that lit up EU 35 in the grid before the customer had touched
     anything, and told the buy button and the stock line to describe it.
   - it forgets the size that was chosen. A server re-render can come back describing the
     first variant, and the grid then jumped off the size the customer had just pressed and
     back onto EU 35 while they were looking at it. Filmed by the shop owner.

   So nothing is chosen until the customer chooses it - by pressing a box, by using the
   theme's own control, or by arriving on a link that names a variant. The select is still
   driven, because the theme needs it for price and stock; it is told, never asked.

   The same principle fixed the worse version: the cart form's hidden variant id, which the
   theme only rewrites when its round-trip returns. A tap on Add to cart inside that window
   used to add the default size. See "the variant".

   THE STRUCTURE IS THE BEHAVIOUR (phone).
   The photographs scroll inside <media-gallery>, a box as tall as a photograph wants to be
   at full width: a swipe over a photograph moves to the next while the summary beneath
   stays put, and when the photographs run out the scroll carries on into the page. The
   summary is fixed to the bottom of the landing screen, covering the top of the product
   information rather than pushing it down, and fades the moment the page moves.

   THINGS LEARNED THE HARD WAY HERE:
   - the scroller has to be <media-gallery>, not the x-cell around it.
   - the summary has to go inside the content cell, not between the cells; those cells are
     items in an x-grid ordered by the theme's media-position setting.
   - the summary must not be in flow. Faded out in flow it still held its height, which read
     as a strange block of white between the last photograph and the title.
   - where the box starts changes without the box changing size - closing the announcement
     bar lifts the photographs 52px - so the offset is re-read on timers after any tap.
   - the frames are sized from the markup's width/height attributes, not from what has
     loaded, or the frame changes height mid-swipe when a later photograph arrives.
   - the button aims at the title, not at the end of the photographs, because the header
     stays pinned to the top of the screen once the page moves.
   - the dots need an IntersectionObserver created AFTER the stylesheet has turned the
     gallery into a scroller.
   - a sold-out size box carries a second line, so the size's name comes from the label span.
*/
(function () {
    'use strict';

    var root = document.documentElement;
    var MIN_PANEL = 150;
    var HIDE_AT = 80;
    var SHOW_AT = 40;
    var SETTLE = 160;      /* how long a hand gets to keep changing its mind */
    var AFTER_ADD = 3500;  /* how long the basket gets before anything re-renders */
    var MAX_NUDGES = 2;    /* how many times we will correct the theme for one choice */
    var STATES = ['none', 'buy', 'notify'];

    /* The customer's choice, held here rather than read back from the page. */
    var chosen = null;     /* { value: 'EU 38', id: '36613383028892' } */
    var corrections = 0;
    var seeded = false;

    function phone() { return window.matchMedia('(max-width: 777px)').matches; }
    function theSelect() { return document.querySelector('variant-selects select, .select__select'); }
    function theTitle() { return document.querySelector('product-content h1, .product-page h1'); }
    function theStock() { return document.querySelector('.inventoryStatus'); }
    function theCart() { return document.querySelector('.cartButton'); }
    function gridPresent() { return !!document.querySelector('[data-dsg]'); }
    function busy() { return root.classList.contains('js-ajax-cart-processing'); }

    function findSize(value) {
        var all = document.querySelectorAll('[data-dsg-size]');
        for (var i = 0; i < all.length; i++) {
            if (all[i].getAttribute('data-value') === value) { return all[i]; }
        }
        return null;
    }

    function findSizeById(id) {
        var all = document.querySelectorAll('[data-dsg-size]');
        for (var i = 0; i < all.length; i++) {
            if (all[i].getAttribute('data-variant-id') === id) { return all[i]; }
        }
        return null;
    }

    function nameOf(btn) {
        if (!btn) { return ''; }
        var label = btn.querySelector('[data-dsg-label]');
        return ((label || btn).textContent || '').trim();
    }

    function take(btn) {
        if (!btn) { return; }
        chosen = {
            value: btn.getAttribute('data-value'),
            id: btn.getAttribute('data-variant-id')
        };
        corrections = 0;
    }

    /* A link that names a variant IS a choice - a back-in-stock email, a shared size, a
       shopping feed. Anything else the server preselected is not. */
    function seedFromLink() {
        if (seeded) { return; }
        seeded = true;
        var id = null;
        try { id = new URLSearchParams(window.location.search).get('variant'); }
        catch (e) { id = null; }
        if (!id) { return; }
        take(findSizeById(id));
    }

    /* =====================================================================
       The variant

       The cart form carries the variant in a hidden input, rewritten by the theme only when
       its server round-trip returns. On a fresh page it holds the default variant, so a tap
       on Add to cart before the round-trip landed used to add the wrong size - choose
       EU 41, get EU 36. Reproduced exactly; the form held 31427865542792 at the press.

       The id is written here instead, from the chosen size: on the tap, after every
       re-render, and once more in the capture phase of the click or submit that uses the
       form, before any of the theme's handlers run. With nothing chosen the press is
       stopped rather than allowed through on whatever the form was holding.
       ===================================================================== */

    function mainForm() {
        var btn = theCart();
        return btn && btn.closest ? btn.closest('form') : null;
    }

    function idInput() {
        var f = mainForm();
        return f ? f.querySelector('input[name="id"]') : null;
    }

    function syncVariant() {
        var input = idInput();
        if (!chosen || !input) { return false; }
        if (input.value !== chosen.id) { input.value = chosen.id; }
        return true;
    }

    var settleTimer = null;

    function tellTheme() {
        var select = theSelect();
        if (!select) { return; }
        if (chosen && select.value !== chosen.value) { select.value = chosen.value; }
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function later(ms) {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(function () {
            settleTimer = null;
            tellTheme();
            reflect();
        }, ms);
    }

    function nudge() {
        var grid = document.querySelector('[data-dsg]');
        var hint = grid ? grid.querySelector('[data-dsg-hint]') : null;
        if (hint) {
            hint.textContent = 'Choose your size first.';
            hint.dpmNudge = true;
            hint.hidden = false;
        }
        var note = grid ? grid.querySelector('.dsg-note') : null;
        if (note) { note.hidden = true; }
        if (grid && grid.scrollIntoView) {
            try { grid.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
            catch (e) { grid.scrollIntoView(); }
        }
    }

    function stop(e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) { e.stopImmediatePropagation(); }
    }

    var replaying = false;

    function replayWhenFree() {
        var tries = 0;
        (function tick() {
            if (!busy()) {
                var btn = theCart();
                if (btn) {
                    replaying = true;
                    try { btn.click(); } finally { replaying = false; }
                }
                return;
            }
            if (++tries < 40) { window.setTimeout(tick, 100); }
        }());
    }

    function guardAdd(e) {
        if (!gridPresent()) { return; }
        if (!syncVariant()) { stop(e); nudge(); return; }
        if (busy() && !replaying) { stop(e); replayWhenFree(); return; }
        later(AFTER_ADD);
    }

    document.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('.cartButton') : null;
        if (btn) { guardAdd(e); }
    }, true);

    document.addEventListener('submit', function (e) {
        var f = e.target;
        if (f && f.querySelector && f.querySelector('.cartButton')) { guardAdd(e); }
    }, true);

    /* =====================================================================
       Painting - all of it from `chosen`
       ===================================================================== */

    function headerBite() {
        var hdr = document.querySelector('header');
        if (!hdr) { return 0; }
        var h = Math.round(hdr.getBoundingClientRect().height);
        return (h > 0 && h < 200) ? h : 0;
    }

    function paint() {
        var size = chosen ? findSize(chosen.value) : null;
        var avail = size ? size.getAttribute('data-available') === 'true' : null;
        var state = !chosen ? 'none' : (avail ? 'buy' : 'notify');

        for (var s = 0; s < STATES.length; s++) {
            root.classList[STATES[s] === state ? 'add' : 'remove']('dpm-state-' + STATES[s]);
        }

        var bis = document.getElementById('BIS_trigger');
        root.classList[bis ? 'add' : 'remove']('dpm-bis');

        var cart = theCart();
        if (cart) {
            if (state !== 'none') { cart.disabled = !avail; }
            if (phone()) {
                var cta = cart.querySelector('.cta');
                var want = state === 'none' ? 'Select your size' : (avail ? 'Add to cart' : 'Sold out');
                if (cta && cta.textContent.trim() !== want) { cta.textContent = want; }
            }
        }

        var inv = theStock();
        if (inv) {
            var saysOut = /out of stock|sold out|udsolgt/i.test(inv.textContent || '');
            var wrong = inv.dpmStale || (state === 'notify' ? !saysOut : saysOut);
            inv.classList[wrong ? 'add' : 'remove']('dpm-hide');
        }

        syncVariant();
    }

    /* Re-applies the choice over whatever the theme last rendered. If the theme's select
       disagrees - which is how the grid used to jump back to EU 35 - it is corrected and
       told again, at most twice per choice so a stubborn disagreement cannot loop. */
    function reflect() {
        var grids = document.querySelectorAll('[data-dsg]');
        root.classList[grids.length ? 'add' : 'remove']('dsg-on');

        if (grids.length) { seedFromLink(); }

        for (var g = 0; g < grids.length; g++) {
            var buttons = grids[g].querySelectorAll('[data-dsg-size]');
            var pick = null;
            for (var i = 0; i < buttons.length; i++) {
                var match = !!chosen && buttons[i].getAttribute('data-value') === chosen.value;
                buttons[i].setAttribute('aria-pressed', match ? 'true' : 'false');
                if (match) { pick = buttons[i]; }
            }

            var hint = grids[g].querySelector('[data-dsg-hint]');
            var note = grids[g].querySelector('.dsg-note');
            if (hint) {
                if (pick && pick.getAttribute('data-available') === 'false') {
                    hint.dpmNudge = false;
                    hint.textContent = nameOf(pick) + ' is sold out. Tap Get notified below' +
                        ' to leave your email and we will write to you the day it is back.';
                    hint.hidden = false;
                } else if (!hint.dpmNudge) {
                    hint.hidden = true;
                }
                if (note) { note.hidden = !hint.hidden; }
            }
        }

        var select = theSelect();
        if (chosen && select && select.value !== chosen.value && corrections < MAX_NUDGES) {
            corrections++;
            select.value = chosen.value;
            later(SETTLE);
        }

        paint();
    }

    function reflectSoon() {
        [60, 250, 600, 1200, 2500].forEach(function (ms) { window.setTimeout(reflect, ms); });
    }

    document.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-dsg-size]') : null;
        if (!btn) { return; }
        e.preventDefault();

        seeded = true;              /* an explicit choice outranks any link */
        take(btn);

        var all = document.querySelectorAll('[data-dsg-size]');
        for (var i = 0; i < all.length; i++) {
            all[i].setAttribute('aria-pressed', all[i] === btn ? 'true' : 'false');
        }
        syncVariant();

        var hint = document.querySelector('[data-dsg-hint]');
        if (hint) { hint.dpmNudge = false; }

        var inv = theStock();
        if (inv) {
            inv.dpmStale = true;
            window.setTimeout(function () { inv.dpmStale = false; reflect(); }, 2600);
        }

        var select = theSelect();
        if (select) { select.value = chosen.value; }
        paint();
        later(SETTLE);
        reflectSoon();
    });

    /* A change the customer made through the theme's own control. Ours already set the
       value before dispatching, so this only adopts something we did not do. */
    document.addEventListener('change', function (e) {
        if (!e.target || !e.target.matches) { return; }
        if (!e.target.matches('variant-selects select, .select__select')) { return; }
        if (!e.isTrusted) { reflect(); reflectSoon(); return; }
        var v = e.target.value;
        if (v && (!chosen || v !== chosen.value)) {
            var box = findSize(v);
            if (box) { seeded = true; take(box); }
        }
        reflect();
        reflectSoon();
    });

    var pending = null;
    function watchBuyArea() {
        var area = document.querySelector('product-info') ||
                   document.querySelector('.bullet-product-content');
        if (!area || area.dpmWatched || !window.MutationObserver) { return; }
        area.dpmWatched = true;
        new MutationObserver(function () {
            syncVariant();
            if (pending) { return; }
            pending = window.setTimeout(function () { pending = null; reflect(); }, 60);
        }).observe(area, { childList: true, subtree: true });
    }

    /* =====================================================================
       Phone: gallery box, dots, summary
       ===================================================================== */

    function initBar(bar) {
        if (bar.dpmReady) { return; }
        bar.dpmReady = true;

        var dotsBox = document.querySelector('[data-dpm-dots]');
        var jumpBtn = bar.querySelector('[data-dpm-jump]');
        var cell = document.querySelector('.bullet-product-media');
        var gallery = cell ? cell.querySelector('media-gallery') : null;
        var content = document.querySelector('.bullet-product-content');
        var shots = gallery ? [].slice.call(gallery.querySelectorAll('picture')) : [];

        if (!cell || !gallery || !content || !shots.length) { return; }

        var many = shots.length > 1;
        var placed = false;
        var watching = false;
        var isAway = false;
        var lastTop = null;

        function place() {
            if (placed || !phone()) { return; }
            content.insertBefore(bar, content.firstChild);
            cell.appendChild(dotsBox);
            placed = true;
        }

        function prepare() {
            for (var i = 0; i < shots.length; i++) {
                var im = shots[i].querySelector('img');
                if (!im) { continue; }
                im.setAttribute('decoding', 'async');
                if (i < 3 && im.getAttribute('loading') === 'lazy') {
                    im.setAttribute('loading', 'eager');
                }
            }
        }

        function aspectOf(im) {
            var w = parseFloat(im.getAttribute('width')) || im.naturalWidth;
            var h = parseFloat(im.getAttribute('height')) || im.naturalHeight;
            return (w > 0 && h > 0) ? h / w : 0;
        }

        function wants(width) {
            var best = 0;
            for (var i = 0; i < shots.length; i++) {
                var im = shots[i].querySelector('img');
                if (!im) { continue; }
                var a = aspectOf(im);
                if (!a) { continue; }
                var h = Math.round(width * a);
                if (h > best) { best = h; }
            }
            return best;
        }

        function setVar(name, px) {
            if (parseInt(root.style.getPropertyValue(name), 10) === px) { return; }
            root.style.setProperty(name, px + 'px');
        }

        function measure() {
            if (!phone()) { return; }
            var box = gallery.getBoundingClientRect();
            var top = Math.round(box.top + window.scrollY);
            var width = Math.round(box.width) || window.innerWidth;
            var room = Math.max(360, window.innerHeight - top);
            var most = room - MIN_PANEL;
            var want = wants(width);

            var shotH = Math.max(240, Math.min(most, want || most));
            lastTop = top;
            setVar('--dpm-gallery', shotH);
            setVar('--dpm-panel', Math.max(MIN_PANEL, room - shotH));
        }

        function guardTop() {
            if (!phone()) { return; }
            var top = Math.round(gallery.getBoundingClientRect().top + window.scrollY);
            if (lastTop !== null && top !== lastTop) { measure(); }
            lastTop = top;
        }

        function guardSoon() {
            [0, 60, 140, 260, 420, 650, 950, 1400].forEach(function (ms) {
                window.setTimeout(guardTop, ms);
            });
        }

        document.addEventListener('click', guardSoon, true);
        window.addEventListener('resize', guardSoon);

        function buildDots() {
            if (!dotsBox || dotsBox.childNodes.length || !many) { return; }
            for (var i = 0; i < shots.length; i++) {
                var d = document.createElement('span');
                d.className = 'dpm-dot';
                d.setAttribute('data-current', i === 0 ? 'true' : 'false');
                dotsBox.appendChild(d);
            }
        }

        function markDot(i) {
            if (!dotsBox || !dotsBox.children.length) { return; }
            for (var k = 0; k < dotsBox.children.length; k++) {
                dotsBox.children[k].setAttribute('data-current', k === i ? 'true' : 'false');
            }
        }

        function watch() {
            if (watching || !many || !('IntersectionObserver' in window)) { return; }
            if (gallery.scrollHeight <= gallery.clientHeight + 10) { return; }
            watching = true;

            var io = new IntersectionObserver(function (entries) {
                for (var e = 0; e < entries.length; e++) {
                    if (entries[e].isIntersecting) { markDot(shots.indexOf(entries[e].target)); }
                }
            }, { root: gallery, threshold: 0.6 });

            for (var s = 0; s < shots.length; s++) { io.observe(shots[s]); }
        }

        function away() {
            if (!phone()) { root.classList.remove('dpm-away'); isAway = false; return; }
            var y = window.scrollY || window.pageYOffset || 0;
            if (y > HIDE_AT && !isAway) {
                isAway = true;
                root.classList.add('dpm-away');
            } else if (y < SHOW_AT && isAway) {
                isAway = false;
                root.classList.remove('dpm-away');
                measure();
            }
        }

        window.addEventListener('scroll', away, { passive: true });

        function goTo(y) {
            if (y < 0) { y = 0; }
            try { window.scrollTo({ top: y, behavior: 'smooth' }); }
            catch (err) { window.scrollTo(0, y); }
        }

        function jump() {
            var title = theTitle();
            goTo(title
                ? title.getBoundingClientRect().top + window.scrollY - headerBite() - 14
                : gallery.getBoundingClientRect().bottom + window.scrollY);
        }

        if (jumpBtn) {
            jumpBtn.addEventListener('click', function (e) { e.preventDefault(); jump(); });
        }

        var startY = null;
        var startAt = 0;

        bar.addEventListener('touchstart', function (e) {
            if (!phone() || !e.touches || e.touches.length !== 1) { startY = null; return; }
            startY = e.touches[0].clientY;
            startAt = window.scrollY || 0;
        }, { passive: true });

        bar.addEventListener('touchend', function (e) {
            if (startY === null || !phone()) { return; }
            var end = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientY : null;
            var moved = (end === null) ? 0 : end - startY;
            startY = null;
            if (moved < -28) { jump(); }
            else if (moved > 28 && startAt < 120) { goTo(0); }
        }, { passive: true });

        function apply() {
            if (phone()) {
                root.classList.add('dpm-sized');
                place();
                prepare();
                buildDots();
                if (dotsBox) { dotsBox.hidden = !many; }
                bar.hidden = false;
                measure();
                watch();
                away();
            } else {
                bar.hidden = true;
                if (dotsBox) { dotsBox.hidden = true; }
                root.classList.remove('dpm-sized');
                root.classList.remove('dpm-away');
                isAway = false;
                root.style.removeProperty('--dpm-gallery');
                root.style.removeProperty('--dpm-panel');
            }
        }

        apply();

        for (var n = 0; n < shots.length; n++) {
            var im = shots[n].querySelector('img');
            if (im && !im.complete) {
                im.addEventListener('load', function () { measure(); watch(); }, { once: true });
            }
        }

        window.addEventListener('resize', apply);
        window.addEventListener('orientationchange', function () { window.setTimeout(apply, 250); });
        window.addEventListener('load', function () { apply(); guardSoon(); });
        window.setTimeout(apply, 900);
        window.setTimeout(function () { measure(); watch(); }, 2000);

        if (window.ResizeObserver) {
            try {
                var ro = new ResizeObserver(function () { measure(); watch(); });
                ro.observe(bar);
                ro.observe(cell);
            } catch (err) { /* the tap guard covers what this would have */ }
        }
    }

    function scan() {
        reflect();
        watchBuyArea();
        var bars = document.querySelectorAll('[data-dpm-bar]');
        for (var i = 0; i < bars.length; i++) { initBar(bars[i]); }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scan);
    } else {
        scan();
    }

    window.addEventListener('load', function () { reflect(); watchBuyArea(); });
    document.addEventListener('shopify:section:load', function () { scan(); reflectSoon(); });
}());